import type { AxiosInstance } from "axios";
import { isAxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/contexts/AxiosContext";
import type {
  CopyToProductionWorkflowRequest,
  CreateProjectRequest,
  CreateProjectResponse,
  DuplicateProjectWorkflowRequest,
  EnhancePromptRequest,
  EnhancePromptResponse,
  EntityAssetExportType,
  ExportEntityAssetsWorkflowRequest,
  GetTextResponse,
  MetadataResponse,
  ProjectListItem,
  SaveTextRequest,
  ScriptGenerationWorkflowRequest,
  ShotGenerationWorkflowRequest,
  StyleDNAExtractionWorkflowRequest,
  TextExtractionWorkflowRequest,
  UploadBookRequest,
  UploadPresignedUrlResponse,
  UserInfoResponse,
  VideoExportWorkflowRequest,
  VideoScriptAnalysisWorkflowRequest,
  WorkflowTransactionResponse
} from "@/types/api";
import type { ProjectDetailsResponse, UpdateProjectRequest, UpdateProjectResponse } from "@/types/project";
import { bumpProjectDetailsTimestamp, PROJECT_STORE_VERSIONS_KEY, withFreshProjectStoreVersions } from "@/services/projectDetails";
import {
  getErrorMessage,
  type MutableEntityType,
  ProjectSaveConflictError,
  type ProjectSaveConflictResponseBody,
  throwLockAwareError
} from "@/api/apiClient";

const fetchUserInfo = async (axiosClient: AxiosInstance): Promise<UserInfoResponse> => {
  try {
    const response = await axiosClient.get<UserInfoResponse>("/user-info");
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when getting user info."));
  }
};

const fetchMetadata = async (axiosClient: AxiosInstance): Promise<MetadataResponse> => {
  try {
    const response = await axiosClient.get<MetadataResponse>("/metadata");
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when getting metadata."));
  }
};

const fetchProjects = async (axiosClient: AxiosInstance): Promise<readonly ProjectListItem[]> => {
  try {
    const response = await axiosClient.get<readonly ProjectListItem[]>("/project");
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when listing projects."));
  }
};

const createProject = async (axiosClient: AxiosInstance, body: CreateProjectRequest): Promise<CreateProjectResponse> => {
  try {
    const response = await axiosClient.post<CreateProjectResponse>("/project", body);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when creating a project."));
  }
};

const requestBookUploadUrl = async (
  axiosClient: AxiosInstance,
  projectId: string,
  body: UploadBookRequest
): Promise<UploadPresignedUrlResponse<"book">> => {
  try {
    const response = await axiosClient.post<UploadPresignedUrlResponse<"book">>(`/uploads/${encodeURIComponent(projectId)}/book`, body);
    return response.data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when requesting a book upload URL.");
  }
};

const archiveProject = async (axiosClient: AxiosInstance, projectId: string): Promise<void> => {
  try {
    await axiosClient.post(`/project/${encodeURIComponent(projectId)}/archive`);
  } catch (error) {
    throwLockAwareError(error, "Something went wrong when archiving the project.");
  }
};

const restoreProject = async (axiosClient: AxiosInstance, projectId: string): Promise<void> => {
  try {
    await axiosClient.post(`/project/${encodeURIComponent(projectId)}/restore`);
  } catch (error) {
    throwLockAwareError(error, "Something went wrong when restoring the project.");
  }
};

/** Owner-only. Once locked, only the owner can write to the project; everyone else stays read-only. */
const lockProject = async (axiosClient: AxiosInstance, projectId: string): Promise<void> => {
  try {
    await axiosClient.post(`/project/${encodeURIComponent(projectId)}/lock`);
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when locking the project."));
  }
};

/** Owner-only. Once unlocked, anyone can write to the project again. */
const unlockProject = async (axiosClient: AxiosInstance, projectId: string): Promise<void> => {
  try {
    await axiosClient.post(`/project/${encodeURIComponent(projectId)}/unlock`);
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when unlocking the project."));
  }
};

const setProjectThumbnail = async (axiosClient: AxiosInstance, projectId: string, imageId: string): Promise<{ readonly thumbnail_url?: string | null }> => {
  try {
    const response = await axiosClient.post<{ readonly thumbnail_url?: string | null }>(`/project/${encodeURIComponent(projectId)}/thumbnail`, {
      image_id: imageId
    });
    return response.data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when setting the project thumbnail.");
  }
};

export const fetchProjectDetails = async (axiosClient: AxiosInstance, projectId: string): Promise<ProjectDetailsResponse> => {
  try {
    const response = await axiosClient.get<ProjectDetailsResponse>(`/project/${encodeURIComponent(projectId)}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when getting project details."));
  }
};

const isProjectDetailsResponse = (value: unknown): value is ProjectDetailsResponse => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const v = value as Partial<ProjectDetailsResponse>;
  return (
    typeof v.project_id === "string" &&
    v.project_id.length > 0 &&
    typeof v.project_name === "string" &&
    typeof v.user_name === "string" &&
    !!v.project &&
    typeof v.project === "object"
  );
};

const updateProject = async (axiosClient: AxiosInstance, projectId: string, body: UpdateProjectRequest): Promise<UpdateProjectResponse> => {
  try {
    // Backend may return the updated object, or may return an empty/ack payload.
    // To avoid breaking UI (e.g. losing `project_id` in cached data), fall back to the request body.
    const { data } = await axiosClient.post<unknown>(`/project/${encodeURIComponent(projectId)}`, body);
    return isProjectDetailsResponse(data) ? data : body;
  } catch (error) {
    if (isAxiosError<ProjectSaveConflictResponseBody>(error) && error.response?.status === 409) {
      const conflictingEntities = Array.isArray(error.response.data?.conflicting_entities) ? error.response.data.conflicting_entities : [];
      throw new ProjectSaveConflictError(error.response.data?.error ?? "Project was concurrently modified.", conflictingEntities);
    }

    return throwLockAwareError(error, "Something went wrong when updating the project.");
  }
};

type WorkflowRequest =
  | TextExtractionWorkflowRequest
  | ScriptGenerationWorkflowRequest
  | ShotGenerationWorkflowRequest
  | StyleDNAExtractionWorkflowRequest
  | VideoScriptAnalysisWorkflowRequest
  | VideoExportWorkflowRequest
  | CopyToProductionWorkflowRequest
  | DuplicateProjectWorkflowRequest
  | ExportEntityAssetsWorkflowRequest;

const startWorkflow = async (axiosClient: AxiosInstance, body: WorkflowRequest, errorMessage: string): Promise<WorkflowTransactionResponse> => {
  try {
    const response = await axiosClient.post<WorkflowTransactionResponse>("/workflow", body);
    return response.data;
  } catch (error) {
    return throwLockAwareError(error, errorMessage);
  }
};

const startTextExtractionWorkflow = async (axiosClient: AxiosInstance, body: TextExtractionWorkflowRequest): Promise<WorkflowTransactionResponse> =>
  startWorkflow(axiosClient, body, "Something went wrong when starting text extraction.");

const startScriptGenerationWorkflow = async (
  axiosClient: AxiosInstance,
  body: ScriptGenerationWorkflowRequest
): Promise<WorkflowTransactionResponse> => startWorkflow(axiosClient, body, "Something went wrong when starting script generation.");

const startCopyToProductionWorkflow = async (
  axiosClient: AxiosInstance,
  body: CopyToProductionWorkflowRequest
): Promise<WorkflowTransactionResponse> => startWorkflow(axiosClient, body, "Something went wrong when starting the copy to production.");

const startDuplicateProjectWorkflow = async (
  axiosClient: AxiosInstance,
  body: DuplicateProjectWorkflowRequest
): Promise<WorkflowTransactionResponse> => startWorkflow(axiosClient, body, "Something went wrong when duplicating the project.");

export const fetchText = async (
  axiosClient: AxiosInstance,
  projectId: string,
  textId: string,
  errorMessage = "Something went wrong when getting extracted text."
): Promise<GetTextResponse> => {
  try {
    const response = await axiosClient.get<GetTextResponse>(`/${encodeURIComponent(projectId)}/text/${encodeURIComponent(textId)}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, errorMessage));
  }
};

const startShotGenerationWorkflow = async (axiosClient: AxiosInstance, body: ShotGenerationWorkflowRequest): Promise<WorkflowTransactionResponse> =>
  startWorkflow(axiosClient, body, "Something went wrong when starting shot generation.");

const startStyleDNAExtractionWorkflow = async (
  axiosClient: AxiosInstance,
  body: StyleDNAExtractionWorkflowRequest
): Promise<WorkflowTransactionResponse> => startWorkflow(axiosClient, body, "Something went wrong when starting Style DNA extraction.");

const startVideoScriptAnalysisWorkflow = async (
  axiosClient: AxiosInstance,
  body: VideoScriptAnalysisWorkflowRequest
): Promise<WorkflowTransactionResponse> => startWorkflow(axiosClient, body, "Something went wrong when starting video script analysis.");

const startVideoExportWorkflow = async (axiosClient: AxiosInstance, body: VideoExportWorkflowRequest): Promise<WorkflowTransactionResponse> =>
  startWorkflow(axiosClient, body, "Something went wrong when starting the video export.");

const startExportEntityAssetsWorkflow = async (
  axiosClient: AxiosInstance,
  body: ExportEntityAssetsWorkflowRequest
): Promise<WorkflowTransactionResponse> => startWorkflow(axiosClient, body, "Something went wrong when starting the export.");

const saveText = async (axiosClient: AxiosInstance, projectId: string, textId: string, body: SaveTextRequest): Promise<{ status: "ok" }> => {
  try {
    const response = await axiosClient.post<{ status: "ok" }>(`/${encodeURIComponent(projectId)}/text/${encodeURIComponent(textId)}`, body);
    return response.data;
  } catch (error) {
    return throwLockAwareError(error, "Something went wrong when saving text.");
  }
};

const releaseProjectLock = async (axiosClient: AxiosInstance, projectId: string): Promise<{ status: "ok" }> => {
  try {
    const response = await axiosClient.post<{ status: "ok" }>(`/release-lock/${encodeURIComponent(projectId)}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when releasing the project lock."));
  }
};

const releaseMutableEntityLock = async (
  axiosClient: AxiosInstance,
  projectId: string,
  entityType: MutableEntityType,
  id: string
): Promise<{ status: "ok" }> => {
  try {
    const response = await axiosClient.post<{ status: "ok" }>(
      `/release-lock/${encodeURIComponent(projectId)}/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}`
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when releasing the entity lock."));
  }
};

const enhancePrompt = async (axiosClient: AxiosInstance, body: EnhancePromptRequest): Promise<EnhancePromptResponse> => {
  try {
    const response = await axiosClient.post<EnhancePromptResponse>("/enhance-prompt", body);
    const enhanced = response.data?.enhanced_prompt;
    if (typeof enhanced !== "string") {
      throw new TypeError("Invalid enhance-prompt response.");
    }

    return { enhanced_prompt: enhanced };
  } catch (error) {
    throw new Error(getErrorMessage(error, "Something went wrong when enhancing the prompt."));
  }
};

// ===== React Query hooks =====

export const useUserInfo = () => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["user-info"],
    queryFn: async () => fetchUserInfo(axiosClient),
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useMetadata = (enabled = true) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["metadata"],
    queryFn: async () => fetchMetadata(axiosClient),
    enabled,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useProjects = () => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["projects"],
    queryFn: async () => fetchProjects(axiosClient),
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useCreateProject = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (body: CreateProjectRequest) => createProject(axiosClient, body)
  });
};

export const useRequestBookUploadUrl = () => {
  const axiosClient = useAxios();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly body: UploadBookRequest }) {
      return requestBookUploadUrl(axiosClient, vars.projectId, vars.body);
    }
  });
};

export const useProjectDetails = (projectId: string | null) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["project", projectId],
    // Stamp `project.timestamp` on every real server response (same as every optimistic-write
    // path already does) so `isProjectDetailsNewerThan` compares apples to apples. Without this,
    // a genuine fresh fetch has no timestamp and loses to a stale-but-timestamped snapshot that
    // React Query's persisted cache rehydrates from IndexedDB on page load - which is what made
    // the Timeline (and everything else reading `actionStore.projectDetails`) revert to
    // pre-reload data after a reload, even though the network response itself was already correct.
    queryFn: async () => bumpProjectDetailsTimestamp(await fetchProjectDetails(axiosClient, projectId!)),
    enabled: typeof projectId === "string" && projectId.length > 0,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useUpdateProject = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly body: UpdateProjectRequest }) {
      try {
        return await updateProject(axiosClient, vars.projectId, vars.body);
      } catch (error) {
        if (!(error instanceof ProjectSaveConflictError)) {
          throw error;
        }

        // Stale baseline on at least one entity — refetch, refresh just the version/hash bookkeeping, retry once.
        const fresh = await fetchProjectDetails(axiosClient, vars.projectId);
        const freshVersions = fresh.project?.[PROJECT_STORE_VERSIONS_KEY];
        const retryBody = withFreshProjectStoreVersions(vars.body, freshVersions) ?? vars.body;
        return await updateProject(axiosClient, vars.projectId, retryBody);
      }
    },
    onSuccess(data, vars) {
      queryClient.setQueryData(["project", vars.projectId], data);

      // Best-effort: keep project list titles in sync without waiting for refetch.
      queryClient.setQueryData<readonly ProjectListItem[] | undefined>(["projects"], (old) => {
        if (!old) {
          return old;
        }

        return old.map((p) => (p.project_id === vars.projectId ? { ...p, name: data.project_name } : p));
      });
    }
  });
};

export const useArchiveProject = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) => archiveProject(axiosClient, projectId),
    onSuccess(_, projectId) {
      queryClient.setQueryData<readonly ProjectListItem[] | undefined>(["projects"], (old) =>
        old?.map((p) => (p.project_id === projectId ? { ...p, is_archived: true } : p))
      );
    }
  });
};

export const useRestoreProject = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) => restoreProject(axiosClient, projectId),
    onSuccess(_, projectId) {
      queryClient.setQueryData<readonly ProjectListItem[] | undefined>(["projects"], (old) =>
        old?.map((p) => (p.project_id === projectId ? { ...p, is_archived: false } : p))
      );
    }
  });
};

export const useLockProject = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { readonly projectId: string; readonly ownerEmail: string }) => lockProject(axiosClient, vars.projectId),
    onSuccess(_, vars) {
      queryClient.setQueryData<readonly ProjectListItem[] | undefined>(["projects"], (old) =>
        old?.map((p) => (p.project_id === vars.projectId ? { ...p, is_locked: true, locked_by: vars.ownerEmail } : p))
      );
      queryClient.setQueryData<ProjectDetailsResponse | undefined>(["project", vars.projectId], (old) => {
        return old ? { ...old, is_locked: true, locked_by: vars.ownerEmail } : old;
      });
    }
  });
};

export const useUnlockProject = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) => unlockProject(axiosClient, projectId),
    onSuccess(_, projectId) {
      queryClient.setQueryData<readonly ProjectListItem[] | undefined>(["projects"], (old) =>
        old?.map((p) => (p.project_id === projectId ? { ...p, is_locked: false, locked_by: null } : p))
      );
      queryClient.setQueryData<ProjectDetailsResponse | undefined>(["project", projectId], (old) => {
        return old ? { ...old, is_locked: false, locked_by: null } : old;
      });
    }
  });
};

export const useSetProjectThumbnail = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { readonly projectId: string; readonly imageId: string }) =>
      setProjectThumbnail(axiosClient, vars.projectId, vars.imageId),
    onSuccess(data, vars) {
      queryClient.setQueryData<readonly ProjectListItem[] | undefined>(["projects"], (old) =>
        old?.map((p) => (p.project_id === vars.projectId ? { ...p, thumbnail_image_id: vars.imageId, thumbnail_url: data.thumbnail_url } : p))
      );
    }
  });
};

export const useStartTextExtractionWorkflow = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (body: TextExtractionWorkflowRequest) => startTextExtractionWorkflow(axiosClient, body)
  });
};

export const useStartScriptGenerationWorkflow = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (body: ScriptGenerationWorkflowRequest) => startScriptGenerationWorkflow(axiosClient, body)
  });
};

export const useStartCopyToProductionWorkflow = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (projectId: string) => startCopyToProductionWorkflow(axiosClient, { project_id: projectId, operation: "CopyToProduction" })
  });
};

export const useStartDuplicateProjectWorkflow = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (projectId: string) => startDuplicateProjectWorkflow(axiosClient, { project_id: projectId, operation: "DuplicateProject" })
  });
};

export const useStartExportEntityAssetsWorkflow = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (body: { projectId: string; entityType: EntityAssetExportType; projectName?: string }) =>
      startExportEntityAssetsWorkflow(axiosClient, {
        project_id: body.projectId,
        operation: "ExportEntityAssets",
        entity_type: body.entityType,
        ...(body.projectName ? { project_name: body.projectName } : {})
      })
  });
};

export const useText = (projectId: string | null, textId: string | null) => {
  const axiosClient = useAxios();
  return useQuery({
    queryKey: ["text", projectId, textId],
    queryFn: async () => fetchText(axiosClient, projectId!, textId!),
    enabled: typeof projectId === "string" && projectId.length > 0 && typeof textId === "string" && textId.length > 0,
    retry: 1,
    refetchOnWindowFocus: false
  });
};

export const useSaveText = () => {
  const axiosClient = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly textId: string; readonly body: SaveTextRequest }) {
      return saveText(axiosClient, vars.projectId, vars.textId, vars.body);
    },
    onSuccess(_, vars) {
      void queryClient.invalidateQueries({ queryKey: ["text", vars.projectId, vars.textId] });
    }
  });
};

export const useReleaseProjectLock = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (projectId: string) => releaseProjectLock(axiosClient, projectId)
  });
};

export const useReleaseMutableEntityLock = () => {
  const axiosClient = useAxios();
  return useMutation({
    async mutationFn(vars: { readonly projectId: string; readonly entityType: MutableEntityType; readonly id: string }) {
      return releaseMutableEntityLock(axiosClient, vars.projectId, vars.entityType, vars.id);
    }
  });
};

export const useStartShotGenerationWorkflow = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (body: ShotGenerationWorkflowRequest) => startShotGenerationWorkflow(axiosClient, body)
  });
};

export const useStartStyleDNAExtractionWorkflow = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (body: StyleDNAExtractionWorkflowRequest) => startStyleDNAExtractionWorkflow(axiosClient, body)
  });
};

export const useStartVideoScriptAnalysisWorkflow = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (body: VideoScriptAnalysisWorkflowRequest) => startVideoScriptAnalysisWorkflow(axiosClient, body)
  });
};

export const useStartVideoExportWorkflow = () => {
  const axiosClient = useAxios();
  return useMutation({
    mutationFn: async (body: VideoExportWorkflowRequest) => startVideoExportWorkflow(axiosClient, body)
  });
};

export const useEnhancePrompt = () => {
  const axiosClient = useAxios();
  return useMutation({
    async mutationFn(vars: {
      readonly projectId: string;
      readonly originalPrompt: string;
      readonly changeText: string;
      readonly referenceImageIds?: readonly string[];
    }) {
      const body: EnhancePromptRequest = {
        "project-id": vars.projectId,
        "original-prompt": vars.originalPrompt,
        "change-text": vars.changeText,
        reference_image_ids: vars.referenceImageIds ?? []
      };
      return enhancePrompt(axiosClient, body);
    }
  });
};
